import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./QuickTradeTicket.css";

const MAX_QUANTITY = 1_000_000;
const MAX_PRICE = 1_000_000;
const MAX_CALCULATION = Number.MAX_SAFE_INTEGER;
const MAX_RISK_REWARD = 1_000_000;
const DECIMAL_PATTERN = /^(?:\d+\.?\d*|\.\d+)$/;
const INTEGER_PATTERN = /^\d+$/;
const CHART_PLAN_EVENT = "nina:chart-trade-plan";

function normalizeQuantityInput(value) {
  const raw = String(value);
  if (raw === "" || raw === "0") return raw;
  return INTEGER_PATTERN.test(raw) ? raw.replace(/^0+(?=\d)/, "") : raw;
}

function plannedQuantityValue(value) {
  const normalized = normalizeQuantityInput(value);
  if (normalized === "" || !INTEGER_PATTERN.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isSafeInteger(number) && number >= 0 && number <= MAX_QUANTITY
    ? number
    : null;
}

function parsePositivePrice(value) {
  const raw = String(value);
  if (!DECIMAL_PATTERN.test(raw)) return null;
  const number = Number(raw);
  return Number.isFinite(number) && number > 0 && number <= MAX_PRICE
    ? number
    : null;
}

function parseQuantity(value) {
  const raw = String(value);
  if (!INTEGER_PATTERN.test(raw)) return null;
  const number = Number(raw);
  return Number.isSafeInteger(number) && number > 0 && number <= MAX_QUANTITY
    ? number
    : null;
}

function safeDerived(value) {
  return Number.isFinite(value) && value >= 0 && value <= MAX_CALCULATION
    ? value
    : null;
}

function validateTicket({
  assetType,
  side,
  quantity,
  entryPrice,
  stopPrice,
  targetOnePrice,
  targetTwoPrice,
  scannerReview,
}) {
  const errors = {};
  const parsed = {
    quantity: parseQuantity(quantity),
    entry: parsePositivePrice(entryPrice),
    stop: parsePositivePrice(stopPrice),
    targetOne: parsePositivePrice(targetOnePrice),
    targetTwo: parsePositivePrice(targetTwoPrice),
  };

  if (side !== "buy" && side !== "sell") {
    errors.side = "Choose Buy or Sell before reviewing the order.";
  }
  if (parsed.quantity === null) {
    errors.quantity =
      `Enter a positive whole-number quantity from 1 to ${MAX_QUANTITY.toLocaleString("en-US")}.`;
  }
  [
    ["entry", parsed.entry, "entry price"],
    ["stop", parsed.stop, "stop price"],
    ["targetOne", parsed.targetOne, "Target 1"],
    ["targetTwo", parsed.targetTwo, "Target 2"],
  ].forEach(([key, value, label]) => {
    if (value === null) {
      errors[key] =
        `Enter a positive ${label} up to $${MAX_PRICE.toLocaleString("en-US")} without scientific notation.`;
    }
  });

  if (scannerReview && assetType === "option") {
    errors.asset =
      "Select a validated contract from the Options Scanner before reviewing an options order.";
  }

  if (parsed.entry !== null && parsed.stop !== null) {
    if (side === "buy" && parsed.stop >= parsed.entry) {
      errors.stop = "For Buy, the stop must be below the entry price.";
    }
    if (side === "sell" && parsed.stop <= parsed.entry) {
      errors.stop = "For Sell, the stop must be above the entry price.";
    }
  }
  if (parsed.entry !== null && parsed.targetOne !== null) {
    if (side === "buy" && parsed.targetOne <= parsed.entry) {
      errors.targetOne = "For Buy, Target 1 must be above the entry price.";
    }
    if (side === "sell" && parsed.targetOne >= parsed.entry) {
      errors.targetOne = "For Sell, Target 1 must be below the entry price.";
    }
  }
  if (parsed.entry !== null && parsed.targetTwo !== null) {
    if (side === "buy" && parsed.targetTwo <= parsed.entry) {
      errors.targetTwo = "For Buy, Target 2 must be above the entry price.";
    }
    if (side === "sell" && parsed.targetTwo >= parsed.entry) {
      errors.targetTwo = "For Sell, Target 2 must be below the entry price.";
    }
  }

  const multiplier = assetType === "option" ? 100 : 1;
  let estimatedCost = null;
  let maximumRisk = null;
  let potentialReward = null;
  let riskReward = null;

  if (!errors.asset && parsed.quantity !== null && parsed.entry !== null) {
    estimatedCost = safeDerived(parsed.quantity * parsed.entry * multiplier);
  }
  if (
    !errors.asset &&
    parsed.quantity !== null &&
    parsed.entry !== null &&
    parsed.stop !== null
  ) {
    const perUnitRisk = Math.abs(parsed.entry - parsed.stop);
    if (Number.isFinite(perUnitRisk) && perUnitRisk > 0) {
      maximumRisk = safeDerived(parsed.quantity * perUnitRisk * multiplier);
    }
  }
  if (
    !errors.asset &&
    parsed.quantity !== null &&
    parsed.entry !== null &&
    parsed.targetOne !== null
  ) {
    const perUnitReward = Math.abs(parsed.targetOne - parsed.entry);
    if (Number.isFinite(perUnitReward) && perUnitReward > 0) {
      potentialReward = safeDerived(
        parsed.quantity * perUnitReward * multiplier
      );
    }
  }
  if (
    maximumRisk !== null &&
    maximumRisk > 0 &&
    potentialReward !== null
  ) {
    const candidate = potentialReward / maximumRisk;
    riskReward =
      Number.isFinite(candidate) && candidate >= 0 && candidate <= MAX_RISK_REWARD
        ? candidate
        : null;
  }

  const inputsComplete = Object.values(parsed).every((value) => value !== null);
  if (
    inputsComplete &&
    !errors.asset &&
    (estimatedCost === null ||
      maximumRisk === null ||
      potentialReward === null ||
      riskReward === null)
  ) {
    errors.calculation =
      "These values exceed the supported calculation range or produce an invalid risk calculation.";
  }

  return {
    errors,
    parsed,
    metrics: { estimatedCost, maximumRisk, potentialReward, riskReward },
    valid: Object.keys(errors).length === 0 &&
      [estimatedCost, maximumRisk, potentialReward, riskReward]
        .every((value) => value !== null && Number.isFinite(value)),
  };
}

function emitChartPlan(detail) {
  window.dispatchEvent(new CustomEvent(CHART_PLAN_EVENT, { detail }));
}

function QuickTradeTicket({
  symbol = "PLUG",
  entry = 0,
  stop = 0,
  targetOne = 0,
  targetTwo = 0,
  recommendedShares = 100,
  confidence = 0,
  tradeBlocked = false,
  ninaSignalColor = "yellow",
  session = "Regular Market",
  accountLabel = "LIVE · Planning only",
  currentPrice = null,
  bid = null,
  ask = null,
  volume = null,
  marketDataSource = "Authenticated quote unavailable",
  onQuantityChange = null,
  scannerReview = false,
}) {
  const [side, setSide] = useState(scannerReview ? null : "buy");
  const [assetType, setAssetType] = useState("stock");
  const [quantity, setQuantity] = useState(
    scannerReview ? "" : recommendedShares
  );
  const [entryPrice, setEntryPrice] = useState(scannerReview ? "" : String(Number(entry)));
const [stopPrice, setStopPrice] = useState(scannerReview ? "" : String(Number(stop)));
const [targetOnePrice, setTargetOnePrice] = useState(scannerReview ? "" : String(Number(targetOne)));
const [targetTwoPrice, setTargetTwoPrice] = useState(scannerReview ? "" : String(Number(targetTwo)));
  const [reviewed, setReviewed] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState({});
  const [chartPlanSet, setChartPlanSet] = useState(false);
  const [orderType, setOrderType] = useState("market");
  const [conditionalOrder, setConditionalOrder] = useState("none");
  const [timeInForce, setTimeInForce] = useState("day");
  const [secondaryOrder, setSecondaryOrder] = useState("none");
  const quantityEditedRef = useRef(false);
  const quantityIdentityRef = useRef(`${symbol}:${scannerReview}`);
  const refs = {
    side: useRef(null),
    quantity: useRef(null),
    entry: useRef(null),
    stop: useRef(null),
    targetOne: useRef(null),
    targetTwo: useRef(null),
    asset: useRef(null),
  };

  const clearChartPlan = () => {
    if (scannerReview) return;
    setChartPlanSet(false);
    emitChartPlan({ symbol, active: false });
  };

  useEffect(() => {
    const nextQuantityIdentity = `${symbol}:${scannerReview}`;
    if (quantityIdentityRef.current !== nextQuantityIdentity) {
      quantityIdentityRef.current = nextQuantityIdentity;
      quantityEditedRef.current = false;
    }
    setSide(scannerReview ? null : "buy");
    setAssetType("stock");
    setQuantity((current) =>
      scannerReview
        ? ""
        : quantityEditedRef.current
        ? current
        : recommendedShares
    );
  setEntryPrice(scannerReview ? "" : String(Number(entry)));
setStopPrice(scannerReview ? "" : String(Number(stop)));
setTargetOnePrice(scannerReview ? "" : String(Number(targetOne)));
setTargetTwoPrice(scannerReview ? "" : String(Number(targetTwo)));
    setReviewed(false);
    setAttempted(false);
    setTouched({});
    setChartPlanSet(false);
    setOrderType("market");
    setConditionalOrder("none");
    setTimeInForce("day");
    setSecondaryOrder("none");
    if (!scannerReview) emitChartPlan({ symbol, active: false });
  }, [entry, recommendedShares, scannerReview, stop, symbol, targetOne, targetTwo]);

  useEffect(() => {
    if (scannerReview || typeof onQuantityChange !== "function") return;
    onQuantityChange({
      symbol,
      quantity: plannedQuantityValue(quantity),
    });
  }, [onQuantityChange, quantity, scannerReview, symbol]);

  const validation = useMemo(
    () =>
      validateTicket({
        assetType,
        side,
        quantity,
        entryPrice,
        stopPrice,
        targetOnePrice,
        targetTwoPrice,
        scannerReview,
      }),
    [
      assetType, side, quantity, entryPrice, stopPrice,
      targetOnePrice, targetTwoPrice, scannerReview,
    ]
  );

  const showError = (key) =>
    Boolean(validation.errors[key] && (attempted || touched[key]));
  const fieldDescription = (key) =>
    showError(key) ? `quick-${symbol}-${key}-error` : undefined;
  const update = (key, setter) => (event) => {
    if (key === "quantity") quantityEditedRef.current = true;
    setter(
      key === "quantity"
        ? normalizeQuantityInput(event.target.value)
        : event.target.value
    );
    setTouched((current) => ({ ...current, [key]: true }));
    setReviewed(false);
    clearChartPlan();
  };
  const chooseSide = (next) => {
    setSide(next);
    setTouched((current) => ({ ...current, stop: true, targetOne: true, targetTwo: true }));
    setReviewed(false);
    clearChartPlan();
  };
  const chooseAsset = (next) => {
    setAssetType(next);
    setTouched((current) => ({ ...current, asset: true }));
    setReviewed(false);
    clearChartPlan();
  };
  const updatePlanningControl = (setter) => (event) => {
    setter(event.target.value);
    setReviewed(false);
    clearChartPlan();
  };
  const chooseOrderType = (next) => {
    setOrderType(next);
    setReviewed(false);
    clearChartPlan();
  };
  const reviewOrder = () => {
    setAttempted(true);
    if (tradeBlocked || !validation.valid) {
      const first = ["side", "asset", "quantity", "entry", "stop", "targetOne", "targetTwo"]
        .find((key) => validation.errors[key]);
      refs[first]?.current?.focus();
      return;
    }
    setReviewed(true);
  };
  const setChartPlan = () => {
    setAttempted(true);
    if (tradeBlocked || !validation.valid || validation.metrics.riskReward < 2) return;
    emitChartPlan({
      active: true,
      symbol,
      side,
      quantity: validation.parsed.quantity,
      entry: validation.parsed.entry,
      stop: validation.parsed.stop,
      target1: validation.parsed.targetOne,
      target2: validation.parsed.targetTwo,
    });
    setChartPlanSet(true);
  };
  const formatMoney = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  const metric = (value) =>
    value !== null && Number.isFinite(value) ? formatMoney(value) : "—";
  const quotePrice = (value) =>
    typeof value === "number" && Number.isFinite(value) && value > 0
      ? formatMoney(value)
      : "—";
  const quoteVolume =
    typeof volume === "number" && Number.isFinite(volume) && volume >= 0
      ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(volume)
      : "—";
const normalizedNinaSignalColor =
  ["green", "yellow", "red"].includes(
    String(ninaSignalColor).toLowerCase()
  )
    ? String(ninaSignalColor).toLowerCase()
    : "yellow";

  const ticket = (
    <section className="quickTradeTicket" data-tour-id="quick-trade">
      <div className="quickTradeHeader">
        <div>
          <span className="quickTradeEyebrow">QUICK TRADE</span>
          <strong>{symbol}</strong>
        </div>
        <span className="quickTradePlanningBadge">PLANNING</span>
      </div>

      {scannerReview && (
        <div className="quickTradeReviewNotice" role="note">
          <strong>Scanner review · simulated</strong>
          <span>Review shortcut only — no order has been placed. Enter and approve every required value.</span>
        </div>
      )}

      {!scannerReview && (
        <div className="quickTradeReviewNotice" role="note">
          <strong>{chartPlanSet ? "Chart trade plan active" : "Chart levels hidden"}</strong>
          <span>
            {chartPlanSet
              ? "Entry, stop, and targets are visible on the chart. Editing any ticket value clears them until you set the plan again."
              : "Entry, stop, and targets stay off the chart until you explicitly set this trade plan. This does not submit an order to Alpaca."}
          </span>
        </div>
      )}

      <div className="quickTradeBody">
        <div className="quickTradeIdentityRows">
          <div className="quickTradeReadRow">
            <span>Account</span>
            <strong>{scannerReview ? "Scanner review · simulated" : accountLabel}</strong>
          </div>
          <div className="quickTradeReadRow">
            <span>Symbol</span>
            <strong>{symbol}</strong>
          </div>
        </div>

        <div className="quickTradeSection">
          <span className="quickTradeSectionLabel">Action</span>
          <div className="quickToggleRow" role="radiogroup" aria-label="Order side"
            aria-invalid={showError("side") || undefined}
            aria-describedby={fieldDescription("side")}>
            <button ref={refs.side} type="button"
              className={side === "buy" ? "quickToggle activeBuy" : "quickToggle"}
              role="radio" aria-checked={side === "buy"}
              onClick={() => chooseSide("buy")}>BUY</button>
            <button type="button" className={side === "sell" ? "quickToggle activeSell" : "quickToggle"}
              role="radio" aria-checked={side === "sell"}
              onClick={() => chooseSide("sell")}>SELL</button>
            {showError("side") && <InlineError id={fieldDescription("side")} message={validation.errors.side} />}
          </div>
        </div>

        <div className="quickTradeSection quickTradeTwoColumn">
          <TicketField label={assetType === "option" ? "Contracts" : "Shares"}
            inputRef={refs.quantity} value={quantity} min="1" max={MAX_QUANTITY} step="1"
            invalid={showError("quantity")} describedBy={fieldDescription("quantity")}
            onChange={update("quantity", setQuantity)} error={validation.errors.quantity} />
          <div className="quickTradeInstrumentField">
            <span>Instrument</span>
            <div className="quickToggleRow" role="radiogroup" aria-label="Asset type"
              aria-invalid={showError("asset") || undefined}
              aria-describedby={fieldDescription("asset")}>
              <button type="button" className={assetType === "stock" ? "quickToggle activeAsset" : "quickToggle"}
                role="radio" aria-checked={assetType === "stock"}
                onClick={() => chooseAsset("stock")}>STOCK</button>
              <button ref={refs.asset} type="button"
                className={assetType === "option" ? "quickToggle activeAsset" : "quickToggle"}
                role="radio" aria-checked={assetType === "option"}
                onClick={() => chooseAsset("option")}>OPTION</button>
              {showError("asset") && <InlineError id={fieldDescription("asset")} message={validation.errors.asset} />}
            </div>
          </div>
        </div>

        <div className="quickTradeSection">
          <span className="quickTradeSectionLabel">Order</span>
          <div className="quickToggleRow" role="radiogroup" aria-label="Order type">
            <button type="button" className={orderType === "market" ? "quickToggle activeAsset" : "quickToggle"}
              role="radio" aria-checked={orderType === "market"}
              onClick={() => chooseOrderType("market")}>MARKET</button>
            <button type="button" className={orderType === "limit" ? "quickToggle activeAsset" : "quickToggle"}
              role="radio" aria-checked={orderType === "limit"}
              onClick={() => chooseOrderType("limit")}>LIMIT</button>
          </div>
          <label className="quickTradeSelectField">
            <span>Conditional order</span>
            <select aria-label="Conditional order" value={conditionalOrder}
              onChange={updatePlanningControl(setConditionalOrder)}>
              <option value="none">None</option>
              <option value="stop-loss">Stop Loss · planning only</option>
              <option value="stop-limit">Stop Limit · planning only</option>
              <option value="trailing-stop">Trailing Stop Loss · planning only</option>
              <option value="trailing-stop-limit">Trail Stop Limit · planning only</option>
            </select>
          </label>
          <small className="quickTradePlanningNote">Order controls prepare a plan only. Nothing is routed to Alpaca.</small>
        </div>

    

        <div className="quickTradeSection quickTradeTwoColumn">
          <label className="quickTradeSelectField">
            <span>Time in force</span>
            <select aria-label="Time in force" value={timeInForce}
              onChange={updatePlanningControl(setTimeInForce)}>
              <option value="day">Day · planning only</option>
              <option value="gtc" disabled>Good 'til Canceled · unavailable</option>
              <option value="day-extended" disabled>Day + Extended · unavailable</option>
              <option value="fok" disabled>Fill or Kill · unavailable</option>
              <option value="ioc" disabled>Immediate or Cancel · unavailable</option>
            </select>
          </label>
          <label className="quickTradeSelectField">
            <span>Secondary order</span>
            <select aria-label="Secondary order" value={secondaryOrder}
              onChange={updatePlanningControl(setSecondaryOrder)}>
              <option value="none">None</option>
              <option value="follow-bid" disabled>Follow bid · unavailable</option>
              <option value="follow-ask" disabled>Follow ask · unavailable</option>
            </select>
          </label>
        </div>

        <div className="quickTradeQuotePanel" aria-label="Authenticated market quote">
          <div className="quickTradeSectionHeading">
            <span>Market quote</span>
            <small>{marketDataSource}</small>
          </div>
          <div className="quickTradeQuoteGrid">
            <div><span>Current</span><strong>{quotePrice(currentPrice)}</strong></div>
            <div><span>Bid</span><strong>{quotePrice(bid)}</strong></div>
            <div><span>Ask</span><strong>{quotePrice(ask)}</strong></div>
            <div><span>Volume</span><strong>{quoteVolume}</strong></div>
          </div>
        </div>

        <div className="quickTradeSummary" aria-label="Trade plan summary">
          <div className="quickTradeMetric"><span>Estimated value</span><strong>{metric(validation.metrics.estimatedCost)}</strong></div>
          <div className="quickTradeMetric"><span>Max risk</span><strong>{metric(validation.metrics.maximumRisk)}</strong></div>
          <div className="quickTradeMetric"><span>Risk / reward</span>
            <strong>{validation.metrics.riskReward === null ? "—" : `${validation.metrics.riskReward.toFixed(2)} : 1`}</strong>
          </div>
          <div className="quickTradeMetric"><span>Confidence</span><strong>{confidence}%</strong></div>
        </div>
      </div>

      {scannerReview && showError("calculation") && (
        <InlineError id={`quick-${symbol}-calculation-error`} message={validation.errors.calculation} />
      )}

      <div className="quickTradeFooter">
        <div className="quickTradeStatus">
          <span className={tradeBlocked ? "quickStatusDot blocked" : "quickStatusDot"} />
          <p>AI Status: <strong>{tradeBlocked ? "Trade Blocked" : "Wait for Confirmation"}</strong></p>
        </div>
        <div className="quickTradeMeta">
          <span>Session / source: {session}</span>
          <span>Planning only · no brokerage order submission</span>
        </div>

        {scannerReview ? (
          <div className="quickReviewActions">
            <button type="button" className="quickDemoExecute"
              aria-disabled={tradeBlocked || !validation.valid}
              onClick={reviewOrder}>Review Order</button>
            <button type="button" className="quickConfirmDisabled" disabled
              aria-describedby="scanner-execution-disabled">Confirm Trade</button>
            <span id="scanner-execution-disabled">
              {reviewed
                ? "Reviewed. Execution remains disabled in simulation mode."
                : "Complete and review the ticket first. Execution is disabled."}
            </span>
          </div>
        ) : (
          <button
            type="button"
            className="quickDemoExecute"
            disabled={tradeBlocked || !validation.valid || validation.metrics.riskReward < 2}
            onClick={setChartPlan}
          >
            <span
  className={`quickPlanSignalLight quickPlanSignalLight--${normalizedNinaSignalColor}`}
  aria-hidden="true"
/>
            {chartPlanSet
              ? `✓ ${side === "buy" ? "Buy" : "Sell"} Plan Set`
              : `Set ${side === "buy" ? "Buy" : "Sell"} Plan`}
          </button>
        )}
      </div>
    </section>
  );

  return ticket;
}

function TicketField({
  label, inputLabel, inputRef, value, min, max, step, invalid, describedBy, onChange, error,
}) {
  return (
    <label className="quickTradeField">
      <span>{label}</span>
      <input ref={inputRef} type="number" min={min} max={max} step={step}
        value={value} onChange={onChange}
        aria-label={inputLabel || label}
        aria-invalid={invalid || undefined} aria-describedby={describedBy} />
      {invalid && <InlineError id={describedBy} message={error} />}
    </label>
  );
}

function InlineError({ id, message }) {
  return <span className="quickTradeError" id={id} role="alert">{message}</span>;
}

export default QuickTradeTicket;
